import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress } from '@mui/material';
import DraggableAppModal from '../Modals/DraggableAppModal';
import UIDateFormater, { DateType } from '../../containers/UIhelper/UIDateFormater';
import useProjectTimezone from '../../hooks/useProjectTimeZone';

const FONT = "'Albert Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const FeedbackHistoryDialog = ({ alertId, history = { data: [], loading: false }, onClose }, context) => {
  const projectTimezone = useProjectTimezone();
  const [sortDir, setSortDir] = useState('desc'); // newest first by default

  // Only feedback entries — not state/priority changes.
  const entries = (history.data || []).filter(e => e.feedback != null && String(e.feedback).trim() !== '');

  const sorted = [...entries].sort((a, b) => {
    const av = Number(a.logging_date) || 0;
    const bv = Number(b.logging_date) || 0;
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  const toggleSort = () => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));

  const formatDate = (loggingDate) => {
    if (loggingDate == null || loggingDate === '') return '';
    const asNum = Number(loggingDate);
    if (!Number.isNaN(asNum)) {
      return UIDateFormater(asNum, DateType.DATE_AND_TIME, 'project', projectTimezone);
    }
    return loggingDate;
  };

  const content = (
    <Box sx={{ minWidth: 440, maxHeight: '60vh', overflowY: 'auto', py: 0.5, fontFamily: FONT }}>
      {history.loading && <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
      {!history.loading && sorted.length === 0 && (
        <Typography sx={{ p: 2, fontSize: '1.05rem' }}>{context.t('no_feedback_history')}</Typography>
      )}
      {!history.loading && sorted.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #d0d0d0' }}>
              <th
                onClick={toggleSort}
                style={{
                  textAlign: 'left', padding: '8px 12px', whiteSpace: 'nowrap', width: '42%',
                  color: '#0A3696', fontWeight: 700, cursor: 'pointer', userSelect: 'none',
                }}
                title={context.t('time')}
              >
                {context.t('time')} <span style={{ opacity: 0.7 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
              </th>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: '#0A3696', fontWeight: 700 }}>
                {context.t('feedback')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((e, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '8px 12px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: '1rem', color: '#222' }}>{formatDate(e.logging_date)}</div>
                  {e.user_name && <div style={{ fontSize: '0.8rem', color: '#888' }}>{e.user_name}</div>}
                </td>
                <td style={{ padding: '8px 12px', verticalAlign: 'top', fontSize: '1rem', color: '#222' }}>
                  {e.feedback}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Box>
  );

  return (
    <DraggableAppModal
      open={true}
      onClose={onClose}
      closeIcon={true}
      hideBackdrop={false}
      maxWidth="sm"
      title={`${context.t('alert_feedback_history')} — ${alertId}`}
      titleClassName="draggable-blue-header"
      content={content}
    />
  );
};

FeedbackHistoryDialog.propTypes = {
  alertId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  history: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};
FeedbackHistoryDialog.contextTypes = { t: PropTypes.func.isRequired };

export default FeedbackHistoryDialog;
